// src/pages/Admin/DossierInscription/components/EditDossierInscriptionDrawer.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaCheck,
  FaExternalLinkAlt,
  FaFileAlt,
  FaGoogleDrive,
  FaSave,
  FaTimes,
  FaTrashAlt,
  FaUpload,
} from "react-icons/fa";
import { toast } from "react-toastify";

import DossierDrawer from "./DossierDrawer";

import {
  deleteDossierFile,
  updateDossierInscription,
  uploadCertificatMedical,
  uploadFormulaireLicenceSigne,
  uploadLicenceDefinitive,
} from "../../../../../services/dossierServices";

import {
  getAdherentName,
  isDriveUrl,
  isServerFile,
  toAbsoluteUrl,
  todayISO,
} from "../dossier.helpers";

const SIGNEE_FIELD = "formulaire_licence_signe";

function isValidUrlOrPath(value) {
  const text = String(value || "").trim();

  if (!text) return true;
  if (text.startsWith("/uploads/")) return true;

  try {
    const url = new URL(text);
    return ["http:", "https:"].includes(url.protocol) && Boolean(url.hostname);
  } catch {
    return false;
  }
}

function isLocalFileUrl(value) {
  return String(value || "").startsWith("/uploads/");
}

function formatDateInput(value) {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  return date.toISOString().slice(0, 10);
}

function FieldLabel({ children, required = false }) {
  return (
    <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
      {children}
      {required ? <span className="ml-1 text-rose-500">*</span> : null}
    </label>
  );
}

function SectionCard({ icon, title, subtitle, rightAddon, children }) {
  return (
    <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/60">
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
            {icon}
          </span>

          <div>
            <h3 className="font-bold text-slate-900 dark:text-white">{title}</h3>

            {subtitle ? (
              <p className="text-sm text-slate-600 dark:text-slate-300">
                {subtitle}
              </p>
            ) : null}
          </div>
        </div>

        {rightAddon ? <div className="shrink-0">{rightAddon}</div> : null}
      </div>

      {children}
    </div>
  );
}

function BigCheck({ checked, onChange, label, helper }) {
  return (
    <label
      className={`inline-flex cursor-pointer items-start gap-3 rounded-2xl border px-4 py-3 transition ${
        checked
          ? "border-emerald-200 bg-emerald-50 dark:border-emerald-500/20 dark:bg-emerald-500/10"
          : "border-slate-200 bg-white hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:hover:bg-slate-900"
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="mt-1 h-5 w-5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-400"
      />

      <span>
        <span className="block text-sm font-bold text-slate-900 dark:text-white">
          {label}
        </span>

        {helper ? (
          <span className="mt-0.5 block text-xs leading-5 text-slate-500 dark:text-slate-400">
            {helper}
          </span>
        ) : null}
      </span>
    </label>
  );
}

function ModeToggle({ value, onChange }) {
  return (
    <div className="inline-flex overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-950">
      <button
        type="button"
        onClick={() => onChange("upload")}
        className={`px-4 py-2 text-xs font-bold transition ${
          value === "upload"
            ? "bg-indigo-600 text-white"
            : "text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-900"
        }`}
      >
        Fichier
      </button>

      <button
        type="button"
        onClick={() => onChange("link")}
        className={`px-4 py-2 text-xs font-bold transition ${
          value === "link"
            ? "bg-indigo-600 text-white"
            : "text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-900"
        }`}
      >
        Lien
      </button>
    </div>
  );
}

function CurrentDoc({ label, url, onOpen, onDelete, onReplaceClick, replacing }) {
  const hasDoc = Boolean(url);
  const drive = isDriveUrl(url);
  const server = isServerFile(url);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-950">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-3">
          <span
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl ${
              hasDoc
                ? "bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300"
                : "bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500"
            }`}
          >
            {drive ? <FaGoogleDrive /> : <FaFileAlt />}
          </span>

          <div className="min-w-0">
            <p className="truncate text-sm font-bold text-slate-900 dark:text-white">
              {label}
            </p>

            <p className="truncate text-xs text-slate-500 dark:text-slate-400">
              {hasDoc
                ? server
                  ? "Fichier serveur"
                  : drive
                    ? "Lien Google Drive"
                    : "Lien externe"
                : "Aucun document"}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onOpen}
            disabled={!hasDoc}
            className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaExternalLinkAlt />
            Ouvrir
          </button>

          {hasDoc ? (
            <button
              type="button"
              onClick={onDelete}
              className="inline-flex items-center gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-bold text-rose-700 transition hover:bg-rose-100 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300 dark:hover:bg-rose-500/20"
            >
              <FaTrashAlt />
              Supprimer
            </button>
          ) : null}
        </div>
      </div>

      {hasDoc && !replacing ? (
        <button
          type="button"
          onClick={onReplaceClick}
          className="mt-3 text-xs font-bold text-indigo-700 underline decoration-dotted underline-offset-4 transition hover:text-indigo-900 dark:text-indigo-300 dark:hover:text-indigo-200"
        >
          Remplacer par un nouveau document
        </button>
      ) : null}
    </div>
  );
}

export default function EditDossierInscriptionDrawer({
  isOpen,
  onClose,
  dossier,
  refreshList,
}) {
  const cmFileRef = useRef(null);
  const signeeFileRef = useRef(null);
  const licenceFileRef = useRef(null);

  const [saving, setSaving] = useState(false);

  const [uploading, setUploading] = useState({
    cm: false,
    signee: false,
    licence: false,
  });

  const [dateCm, setDateCm] = useState("");
  const [validateCM, setValidateCM] = useState(false);
  const [validateLicence, setValidateLicence] = useState(false);
  const [validateReglement, setValidateReglement] = useState(false);

  const [cmUrl, setCmUrl] = useState("");
  const [formSignerUrl, setFormSignerUrl] = useState("");
  const [formSigneeUrl, setFormSigneeUrl] = useState("");
  const [licenceUrl, setLicenceUrl] = useState("");
  const [driveUrl, setDriveUrl] = useState("");

  const [cmMode, setCmMode] = useState("upload");
  const [cmFile, setCmFile] = useState(null);
  const [cmReplacing, setCmReplacing] = useState(false);

  const [signeeMode, setSigneeMode] = useState("upload");
  const [signeeFile, setSigneeFile] = useState(null);
  const [signeeReplacing, setSigneeReplacing] = useState(false);

  const [licenceMode, setLicenceMode] = useState("upload");
  const [licenceFile, setLicenceFile] = useState(null);
  const [licenceReplacing, setLicenceReplacing] = useState(false);

  const adherentName = useMemo(() => {
    return dossier ? getAdherentName(dossier) : "Adhérent";
  }, [dossier]);

  useEffect(() => {
    if (!isOpen || !dossier) return;

    setSaving(false);

    setDateCm(formatDateInput(dossier?.date_cm));
    setValidateCM(Boolean(dossier?.validate_cm));
    setValidateLicence(Boolean(dossier?.validate_licence));
    setValidateReglement(Boolean(dossier?.validate_reglement));

    setCmUrl(dossier?.cm_url || "");
    setFormSignerUrl(dossier?.formulaire_licence_a_signer || "");
    setFormSigneeUrl(
      dossier?.formulaire_licence_signe ||
        dossier?.formulaire_licence_signee ||
        ""
    );
    setLicenceUrl(dossier?.licence_url || dossier?.licence_assurance_url || "");
    setDriveUrl(dossier?.lien_drive || "");

    setCmMode(dossier?.cm_url && isLocalFileUrl(dossier.cm_url) ? "upload" : "link");
    setSigneeMode(
      dossier?.formulaire_licence_signe &&
        isLocalFileUrl(dossier.formulaire_licence_signe)
        ? "upload"
        : "link"
    );
    setLicenceMode(
      dossier?.licence_url && isLocalFileUrl(dossier.licence_url)
        ? "upload"
        : "link"
    );

    setCmFile(null);
    setSigneeFile(null);
    setLicenceFile(null);

    setCmReplacing(false);
    setSigneeReplacing(false);
    setLicenceReplacing(false);

    if (cmFileRef.current) cmFileRef.current.value = "";
    if (signeeFileRef.current) signeeFileRef.current.value = "";
    if (licenceFileRef.current) licenceFileRef.current.value = "";
  }, [isOpen, dossier]);

  if (!dossier?.id) return null;

  const openUrl = (url) => {
    if (!url) return;
    window.open(toAbsoluteUrl(url), "_blank", "noopener,noreferrer");
  };

  const removeField = async (field, label) => {
    if (!window.confirm(`Supprimer « ${label} » ?`)) return;

    try {
      setSaving(true);

      await deleteDossierFile(dossier.id, field);

      if (field === "cm_url") setCmUrl("");
      if (field === "formulaire_licence_a_signer") setFormSignerUrl("");
      if (field === SIGNEE_FIELD) setFormSigneeUrl("");
      if (field === "licence_url") setLicenceUrl("");
      if (field === "lien_drive") setDriveUrl("");

      toast.success("Document supprimé.");

      await refreshList?.();
    } catch (error) {
      console.error("Erreur suppression document :", error);
      toast.error("Échec de la suppression.");
    } finally {
      setSaving(false);
    }
  };

  const submitCM = async () => {
    try {
      if (!dateCm) {
        toast.error("Indique la date du certificat médical.");
        return;
      }

      setUploading((prev) => ({ ...prev, cm: true }));

      if (cmMode === "upload") {
        if (!cmFile) {
          toast.error("Choisis un fichier certificat médical.");
          return;
        }

        const data = await uploadCertificatMedical(dossier.id, cmFile, {
          date_cm: dateCm,
        });

        setCmUrl(data?.cm_url || "");
        setDateCm(formatDateInput(data?.date_cm) || dateCm);

        toast.success("Certificat médical téléversé.");
      } else {
        if (!isValidUrlOrPath(cmUrl)) {
          toast.error("URL du certificat médical invalide.");
          return;
        }

        await updateDossierInscription(dossier.id, {
          cm_url: cmUrl.trim() || null,
          date_cm: dateCm,
        });

        toast.success("Lien du certificat médical enregistré.");
      }

      setCmReplacing(false);
      setCmFile(null);
      if (cmFileRef.current) cmFileRef.current.value = "";

      await refreshList?.();
    } catch (error) {
      console.error("Erreur certificat médical :", error);
      toast.error("Échec sur le certificat médical.");
    } finally {
      setUploading((prev) => ({ ...prev, cm: false }));
    }
  };

  const submitFormSigner = async () => {
    try {
      if (!isValidUrlOrPath(formSignerUrl)) {
        toast.error("URL du formulaire à signer invalide.");
        return;
      }

      setSaving(true);

      await updateDossierInscription(dossier.id, {
        formulaire_licence_a_signer: formSignerUrl.trim() || null,
      });

      toast.success("Lien du formulaire à signer enregistré.");
      await refreshList?.();
    } catch (error) {
      console.error("Erreur formulaire à signer :", error);
      toast.error("Échec de la mise à jour.");
    } finally {
      setSaving(false);
    }
  };

  const submitSignee = async () => {
    try {
      setUploading((prev) => ({ ...prev, signee: true }));

      if (signeeMode === "upload") {
        if (!signeeFile) {
          toast.error("Choisis un fichier formulaire signé.");
          return;
        }

        const data = await uploadFormulaireLicenceSigne(dossier.id, signeeFile);

        const newUrl =
          data?.formulaire_licence_signe ||
          data?.formulaire_licence_signee ||
          data?.url ||
          "";

        setFormSigneeUrl(newUrl);

        toast.success("Formulaire signé téléversé.");
      } else {
        if (!isValidUrlOrPath(formSigneeUrl)) {
          toast.error("URL du formulaire signé invalide.");
          return;
        }

        await updateDossierInscription(dossier.id, {
          formulaire_licence_signe: formSigneeUrl.trim() || null,
        });

        toast.success("Lien du formulaire signé enregistré.");
      }

      setSigneeReplacing(false);
      setSigneeFile(null);
      if (signeeFileRef.current) signeeFileRef.current.value = "";

      await refreshList?.();
    } catch (error) {
      console.error("Erreur formulaire signé :", error);
      toast.error("Échec sur le formulaire signé.");
    } finally {
      setUploading((prev) => ({ ...prev, signee: false }));
    }
  };

  const submitLicence = async () => {
    try {
      setUploading((prev) => ({ ...prev, licence: true }));

      if (licenceMode === "upload") {
        if (!licenceFile) {
          toast.error("Choisis un fichier licence.");
          return;
        }

        const data = await uploadLicenceDefinitive(dossier.id, licenceFile);

        setLicenceUrl(data?.licence_url || data?.url || "");

        toast.success("Licence téléversée.");
      } else {
        if (!isValidUrlOrPath(licenceUrl)) {
          toast.error("URL de la licence invalide.");
          return;
        }

        await updateDossierInscription(dossier.id, {
          licence_url: licenceUrl.trim() || null,
        });

        toast.success("Lien de la licence enregistré.");
      }

      setLicenceReplacing(false);
      setLicenceFile(null);
      if (licenceFileRef.current) licenceFileRef.current.value = "";

      await refreshList?.();
    } catch (error) {
      console.error("Erreur licence :", error);
      toast.error("Échec sur la licence.");
    } finally {
      setUploading((prev) => ({ ...prev, licence: false }));
    }
  };

  const submitMeta = async (event) => {
    event?.preventDefault?.();

    try {
      if (!isValidUrlOrPath(formSignerUrl)) {
        toast.error("URL du formulaire à signer invalide.");
        return;
      }

      if (!isValidUrlOrPath(driveUrl)) {
        toast.error("URL Drive invalide.");
        return;
      }

      setSaving(true);

      const payload = {
        validate_cm: Boolean(validateCM),
        validate_licence: Boolean(validateLicence),
        validate_reglement: Boolean(validateReglement),
        formulaire_licence_a_signer: formSignerUrl.trim() || null,
        formulaire_licence_signe: formSigneeUrl.trim() || null,
        lien_drive: driveUrl.trim() || null,
        date_cm: validateCM ? dateCm || todayISO() : null,
      };

      await updateDossierInscription(dossier.id, payload);

      toast.success("Dossier mis à jour.");

      await refreshList?.();
      onClose?.();
    } catch (error) {
      console.error("Erreur mise à jour dossier :", error);
      toast.error("Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DossierDrawer
      isOpen={isOpen}
      onClose={onClose}
      title="Modifier un dossier d'inscription"
      subtitle={adherentName}
      width="max-w-6xl"
    >
      <form onSubmit={submitMeta} className="flex min-h-full flex-col">
        <div className="flex-1 space-y-5 p-4 sm:p-6">
          <SectionCard
            icon={<FaFileAlt />}
            title="Certificat médical"
            subtitle="Gérez le certificat, sa date et sa validation."
            rightAddon={
              <BigCheck
                checked={validateCM}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setValidateCM(checked);

                  if (checked && !dateCm) {
                    setDateCm(todayISO());
                  }

                  if (!checked) {
                    setDateCm("");
                  }
                }}
                label="CM validé"
              />
            }
          >
            <div className="space-y-4">
              <div className="grid gap-4 lg:grid-cols-[1fr_220px]">
                <CurrentDoc
                  label="Certificat médical actuel"
                  url={cmUrl}
                  onOpen={() => openUrl(cmUrl)}
                  onDelete={() => removeField("cm_url", "Certificat médical")}
                  onReplaceClick={() => setCmReplacing(true)}
                  replacing={cmReplacing}
                />

                <div>
                  <FieldLabel>Date CM</FieldLabel>
                  <input
                    type="date"
                    value={dateCm}
                    onChange={(event) => setDateCm(event.target.value)}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  />
                </div>
              </div>

              {(!cmUrl || cmReplacing) ? (
                <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-950">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                    <ModeToggle value={cmMode} onChange={setCmMode} />

                    {cmReplacing ? (
                      <button
                        type="button"
                        onClick={() => setCmReplacing(false)}
                        className="text-xs font-bold text-slate-500 underline decoration-dotted underline-offset-4 transition hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                      >
                        Annuler le remplacement
                      </button>
                    ) : null}
                  </div>

                  {cmMode === "upload" ? (
                    <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
                      <div>
                        <FieldLabel>Fichier PDF / image</FieldLabel>
                        <input
                          ref={cmFileRef}
                          type="file"
                          accept=".pdf,image/*"
                          onChange={(event) =>
                            setCmFile(event.target.files?.[0] || null)
                          }
                          className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                        />
                      </div>

                      <button
                        type="button"
                        onClick={submitCM}
                        disabled={uploading.cm || !cmFile || !dateCm}
                        className="inline-flex items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <FaUpload />
                        {uploading.cm ? "Upload..." : "Enregistrer"}
                      </button>
                    </div>
                  ) : (
                    <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
                      <div>
                        <FieldLabel>Lien certificat</FieldLabel>
                        <input
                          type="text"
                          value={cmUrl}
                          onChange={(event) => setCmUrl(event.target.value)}
                          placeholder="https://... ou /uploads/..."
                          className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                        />
                      </div>

                      <button
                        type="button"
                        onClick={submitCM}
                        disabled={uploading.cm || !dateCm}
                        className="inline-flex items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <FaSave />
                        {uploading.cm ? "Enregistrement..." : "Enregistrer"}
                      </button>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </SectionCard>

          <SectionCard
            icon={<FaFileAlt />}
            title="Licence & assurance"
            subtitle="Formulaire à signer, formulaire signé et licence définitive."
            rightAddon={
              <BigCheck
                checked={validateLicence}
                onChange={(event) => setValidateLicence(event.target.checked)}
                label="Licence validée"
              />
            }
          >
            <div className="space-y-5">
              <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-950">
                <CurrentDoc
                  label="Formulaire à signer"
                  url={formSignerUrl}
                  onOpen={() => openUrl(formSignerUrl)}
                  onDelete={() =>
                    removeField(
                      "formulaire_licence_a_signer",
                      "Formulaire à signer"
                    )
                  }
                  onReplaceClick={() => {}}
                  replacing={false}
                />

                <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
                  <div>
                    <FieldLabel>Lien du formulaire à signer</FieldLabel>
                    <input
                      type="text"
                      value={formSignerUrl}
                      onChange={(event) => setFormSignerUrl(event.target.value)}
                      placeholder="https://... ou /uploads/..."
                      className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                    />
                  </div>

                  <button
                    type="button"
                    onClick={submitFormSigner}
                    disabled={saving}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <FaSave />
                    Enregistrer
                  </button>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-950">
                <CurrentDoc
                  label="Formulaire signé"
                  url={formSigneeUrl}
                  onOpen={() => openUrl(formSigneeUrl)}
                  onDelete={() => removeField(SIGNEE_FIELD, "Formulaire signé")}
                  onReplaceClick={() => setSigneeReplacing(true)}
                  replacing={signeeReplacing}
                />

                {(!formSigneeUrl || signeeReplacing) ? (
                  <div className="mt-4">
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                      <ModeToggle value={signeeMode} onChange={setSigneeMode} />

                      {signeeReplacing ? (
                        <button
                          type="button"
                          onClick={() => setSigneeReplacing(false)}
                          className="text-xs font-bold text-slate-500 underline decoration-dotted underline-offset-4 transition hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                        >
                          Annuler le remplacement
                        </button>
                      ) : null}
                    </div>

                    {signeeMode === "upload" ? (
                      <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
                        <div>
                          <FieldLabel>Fichier signé</FieldLabel>
                          <input
                            ref={signeeFileRef}
                            type="file"
                            accept=".pdf,image/*"
                            onChange={(event) =>
                              setSigneeFile(event.target.files?.[0] || null)
                            }
                            className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                          />
                        </div>

                        <button
                          type="button"
                          onClick={submitSignee}
                          disabled={uploading.signee || !signeeFile}
                          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <FaUpload />
                          {uploading.signee ? "Upload..." : "Enregistrer"}
                        </button>
                      </div>
                    ) : (
                      <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
                        <div>
                          <FieldLabel>Lien formulaire signé</FieldLabel>
                          <input
                            type="text"
                            value={formSigneeUrl}
                            onChange={(event) =>
                              setFormSigneeUrl(event.target.value)
                            }
                            placeholder="https://... ou /uploads/..."
                            className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                          />
                        </div>

                        <button
                          type="button"
                          onClick={submitSignee}
                          disabled={uploading.signee}
                          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <FaSave />
                          {uploading.signee ? "Enregistrement..." : "Enregistrer"}
                        </button>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-950">
                <CurrentDoc
                  label="Licence / Assurance"
                  url={licenceUrl}
                  onOpen={() => openUrl(licenceUrl)}
                  onDelete={() => removeField("licence_url", "Licence")}
                  onReplaceClick={() => setLicenceReplacing(true)}
                  replacing={licenceReplacing}
                />

                {(!licenceUrl || licenceReplacing) ? (
                  <div className="mt-4">
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                      <ModeToggle value={licenceMode} onChange={setLicenceMode} />

                      {licenceReplacing ? (
                        <button
                          type="button"
                          onClick={() => setLicenceReplacing(false)}
                          className="text-xs font-bold text-slate-500 underline decoration-dotted underline-offset-4 transition hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                        >
                          Annuler le remplacement
                        </button>
                      ) : null}
                    </div>

                    {licenceMode === "upload" ? (
                      <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
                        <div>
                          <FieldLabel>Fichier licence</FieldLabel>
                          <input
                            ref={licenceFileRef}
                            type="file"
                            accept=".pdf,image/*"
                            onChange={(event) =>
                              setLicenceFile(event.target.files?.[0] || null)
                            }
                            className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                          />
                        </div>

                        <button
                          type="button"
                          onClick={submitLicence}
                          disabled={uploading.licence || !licenceFile}
                          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <FaUpload />
                          {uploading.licence ? "Upload..." : "Enregistrer"}
                        </button>
                      </div>
                    ) : (
                      <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
                        <div>
                          <FieldLabel>Lien licence</FieldLabel>
                          <input
                            type="text"
                            value={licenceUrl}
                            onChange={(event) => setLicenceUrl(event.target.value)}
                            placeholder="https://... ou /uploads/..."
                            className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                          />
                        </div>

                        <button
                          type="button"
                          onClick={submitLicence}
                          disabled={uploading.licence}
                          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <FaSave />
                          {uploading.licence ? "Enregistrement..." : "Enregistrer"}
                        </button>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          </SectionCard>

          <SectionCard
            icon={<FaGoogleDrive />}
            title="Drive & règlement"
            subtitle="Lien Drive, validation du règlement et sauvegarde globale."
            rightAddon={
              <BigCheck
                checked={validateReglement}
                onChange={(event) => setValidateReglement(event.target.checked)}
                label="Règlement validé"
              />
            }
          >
            <div className="space-y-4">
              <CurrentDoc
                label="Dossier Google Drive"
                url={driveUrl}
                onOpen={() => openUrl(driveUrl)}
                onDelete={() => removeField("lien_drive", "Lien Drive")}
                onReplaceClick={() => {}}
                replacing={false}
              />

              <div>
                <FieldLabel>Lien Drive</FieldLabel>
                <input
                  type="text"
                  value={driveUrl}
                  onChange={(event) => setDriveUrl(event.target.value)}
                  placeholder="https://drive.google.com/..."
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>
            </div>
          </SectionCard>

          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-xs leading-5 text-emerald-800 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300">
            <span className="inline-flex items-center gap-2 font-bold">
              <FaCheck />
              Important :
            </span>{" "}
            les boutons “Enregistrer” dans chaque bloc sauvegardent uniquement le
            document concerné. Le bouton final sauvegarde les validations, la date
            CM, le formulaire à signer, le formulaire signé et le lien Drive.
          </div>
        </div>

        <div className="flex flex-col gap-2 border-t border-slate-200 p-4 dark:border-slate-800 sm:flex-row sm:justify-end sm:px-6">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaTimes />
            Annuler
          </button>

          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FaSave />
            {saving ? "Enregistrement..." : "Enregistrer les validations & liens"}
          </button>
        </div>
      </form>
    </DossierDrawer>
  );
}